import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Shield, Truck, Package, Clock, Thermometer, Box } from 'lucide-react';

const WhyLamaziNew = ({ content }) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const metrics = [
    { label: '24-48h Dispatch', icon: Clock },
    { label: 'Cold Chain Monitored', icon: Thermometer },
    { label: 'Flexible Packaging', icon: Box }
  ];

  const features = [
    {
      icon: Shield,
      title: content.why.features[0].title,
      description: content.why.features[0].description
    },
    {
      icon: Thermometer,
      title: content.why.features[1].title,
      description: content.why.features[1].description
    },
    {
      icon: Truck,
      title: content.why.features[2].title,
      description: content.why.features[2].description
    },
    {
      icon: Package,
      title: content.why.features[3].title,
      description: content.why.features[3].description
    }
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-32 overflow-hidden"
    >
      {/* Dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-transparent" />
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          {/* Left Side: Headline + Bullets */}
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 -translate-x-10'
            }`}
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {content.why.title}
            </h2>
            <p className="text-xl text-slate-300 mb-10 font-light leading-relaxed">
              {content.why.subtitle}
            </p>

            {/* Key Bullets */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                <p className="text-slate-200 text-lg">
                  Multi-stage quality inspection from farm to delivery
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                <p className="text-slate-200 text-lg">
                  Temperature-controlled logistics across Europe
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                <p className="text-slate-200 text-lg">
                  Custom branding and packaging solutions
                </p>
              </div>
            </div>
          </div>

          {/* Right Side: 2x2 Feature Grid */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className={`group p-8 bg-white/10 backdrop-blur-sm md:backdrop-blur-md rounded-2xl border border-white/20 hover:border-emerald-500/40 hover:bg-white/15 transition-all duration-500 shadow-md md:shadow-lg ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ 
                      transitionDelay: `${index * 100 + 400}ms`,
                      filter: isVisible ? 'blur(0px)' : 'blur(5px)'
                    }}
                  >
                    <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300">
                      <Icon className="w-7 h-7 text-emerald-400 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-emerald-300 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-slate-200 leading-relaxed text-sm">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Metrics Row */}
        <div
          className={`mt-20 transition-all duration-1000 delay-600 ${
            isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 bg-white/15 backdrop-blur-sm md:backdrop-blur-md rounded-xl border border-white/25 shadow-md md:shadow-lg"
                >
                  <Icon className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  <p className="text-white font-semibold text-lg">{metric.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyLamaziNew;
